import type { ReactNode } from 'react'
import { FaGithub, FaLinkedin } from 'react-icons/fa'
import { FaXTwitter } from 'react-icons/fa6'
import { SiNpm } from 'react-icons/si'
import { EmailCapture, OpenInAI, type OpenInAIAction, type OpenInAIPlatform } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { useLink } from '../../lib/link-context'

interface FooterLargeProps {
  /** Logo as image object (with optional title for icon+text), or plain string text */
  logo?:
    | {
        url: string
        src: string
        alt: string
        /** Optional text displayed next to the logo image */
        title?: string
      }
    | string
  className?: string
  sections?: Array<{
    title: string
    links: Array<{ name: string; href: string }>
  }>
  socialLinks?: Array<{
    icon: ReactNode
    href: string
    label: string
  }>
  /** Domain name for copyright (e.g., "dotdo.ai") */
  domain?: string
  legalLinks?: Array<{
    name: string
    href: string
  }>
  /** Show email capture form */
  showEmailCapture?: boolean
  /** Email capture placeholder */
  emailPlaceholder?: string
  /** Email capture button text */
  emailButtonText?: string
  /** Email capture submit handler */
  onEmailSubmit?: (email: string) => void | Promise<void>
  /** Email capture subcopy (e.g., privacy policy notice) */
  emailSubcopy?: ReactNode
  /** Show "Open in AI" feature next to social icons */
  showOpenInAI?: boolean
  /** OpenInAI variant: 'icons' shows all AI platform icons, 'dropdown' shows a single button */
  openInAIVariant?: 'icons' | 'dropdown'
  /** OpenInAI actions for icons variant */
  openInAIActions?: OpenInAIAction[]
  /** OpenInAI default prompt for dropdown variant */
  openInAIDefaultPrompt?: string
  /** OpenInAI platforms to show */
  openInAIPlatforms?: OpenInAIPlatform[]
  /** Page title for OpenInAI context */
  pageTitle?: string
}

const defaultSections = [
  {
    title: 'Product',
    links: [
      { name: 'API', href: '#' },
      { name: 'CLI', href: '#' },
      { name: 'SDK', href: '#' },
      { name: 'MCP', href: '#' },
    ],
  },
  {
    title: 'Developers',
    links: [
      { name: 'Docs', href: '#' },
      { name: 'Quickstart', href: '#' },
      { name: 'Changelog', href: '#' },
      { name: 'Status', href: '#' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { name: 'Blog', href: '#' },
      { name: 'Community', href: '#' },
      { name: 'Support', href: '#' },
      { name: 'Partners', href: '#' },
    ],
  },
  {
    title: 'Company',
    links: [
      { name: 'About', href: '#' },
      { name: 'Careers', href: '#' },
      { name: 'Press', href: '#' },
      { name: 'Contact', href: '#' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { name: 'Terms', href: '#' },
      { name: 'Privacy', href: '#' },
      { name: 'Cookies', href: '#' },
      { name: 'Licenses', href: '#' },
    ],
  },
  {
    title: 'Connect',
    links: [
      { name: 'Twitter', href: '#' },
      { name: 'LinkedIn', href: '#' },
      { name: 'GitHub', href: '#' },
      { name: 'Discord', href: '#' },
    ],
  },
]

// X and LinkedIn always point to the .do brand accounts (fixed defaults).
// GitHub and npm are per-product — override them via the `socialLinks` prop.
const defaultSocialLinks = [
  { icon: <FaGithub className="size-5" />, href: '#', label: 'GitHub' },
  { icon: <SiNpm className="size-5" />, href: '#', label: 'npm' },
  { icon: <FaXTwitter className="size-5" />, href: 'https://x.com/dotdo_ai', label: 'X' },
  { icon: <FaLinkedin className="size-5" />, href: 'https://www.linkedin.com/company/dotdo', label: 'LinkedIn' },
]

const defaultLegalLinks = [
  { name: 'Terms', href: '#' },
  { name: 'Privacy', href: '#' },
]

const FooterLarge = ({
  logo = {
    url: '/',
    src: '/logos/mdxui.svg',
    alt: 'Logo',
  },
  sections = defaultSections,
  socialLinks = defaultSocialLinks,
  domain = '.do',
  legalLinks = defaultLegalLinks,
  showEmailCapture = false,
  emailPlaceholder,
  emailButtonText = 'Subscribe',
  onEmailSubmit,
  emailSubcopy,
  showOpenInAI = false,
  openInAIVariant = 'dropdown',
  openInAIActions,
  openInAIDefaultPrompt,
  openInAIPlatforms,
  pageTitle,
  className,
}: FooterLargeProps) => {
  const Link = useLink()
  return (
    <section className={cn('py-16 sm:py-24 lg:py-32', className)}>
      <div className="container mx-auto max-w-7xl">
        {/* Top row: Logo + social icons left, email capture right */}
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
          <div className={cn(
            "flex flex-col gap-4",
            showEmailCapture
              ? "sm:gap-4"
              : "w-full sm:flex-row sm:items-center sm:justify-between sm:gap-6"
          )}>
            {typeof logo === 'string' ? (
              <span className="text-lg font-semibold">{logo}</span>
            ) : (
              <Link href={logo.url} className="flex items-center gap-1.5">
                {logo.src && (
                  <span className="inline-flex rounded-[var(--radius)] bg-accent/50">
                    <img src={logo.src} alt={logo.alt} className="dark:invert" />
                  </span>
                )}
                {logo.title && (
                  <span className="text-lg font-semibold">{logo.title}</span>
                )}
              </Link>
            )}
            <div className="flex items-center gap-6">
              <ul className="flex items-center space-x-6 text-muted-foreground">
                {socialLinks.map((social, idx) => (
                  <li key={idx} className="font-medium hover:text-foreground">
                    <a href={social.href} aria-label={social.label}>
                      {social.icon}
                    </a>
                  </li>
                ))}
              </ul>
              {showOpenInAI && (
                <>
                  <div className="h-5 w-px bg-border" />
                  <OpenInAI
                    variant={openInAIVariant}
                    actions={openInAIActions}
                    defaultPrompt={openInAIDefaultPrompt}
                    platforms={openInAIPlatforms}
                    pageTitle={pageTitle}
                  />
                </>
              )}
            </div>
          </div>
          {showEmailCapture && (
            <EmailCapture
              placeholder={emailPlaceholder}
              buttonText={emailButtonText}
              onSubmit={onEmailSubmit}
              subcopy={emailSubcopy}
            />
          )}
        </div>

        {/* Divider */}
        <div className="my-10 border-t border-border" />

        {/* Six columns: 2x3 on mobile, 6 on desktop */}
        <div className="grid grid-cols-2 gap-8 lg:grid-cols-6">
          {sections.map((section, sectionIdx) => (
            <div key={sectionIdx}>
              <h3 className="mb-4 text-sm font-medium">{section.title}</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                {section.links.map((link, linkIdx) => (
                  <li
                    key={linkIdx}
                    className="font-medium hover:text-foreground"
                  >
                    <Link href={link.href}>{link.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom row: Copyright and legal links */}
        <div className="mt-12 flex flex-col justify-between gap-4 border-t border-border pt-8 text-xs font-medium text-muted-foreground md:flex-row md:items-center">
          <p>
            © {domain} {new Date().getFullYear()} All rights reserved.
          </p>
          <ul className="flex flex-col gap-2 md:flex-row">
            {legalLinks.map((link, idx) => (
              <li key={idx} className="hover:text-foreground">
                <Link href={link.href}>{link.name}</Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

export { FooterLarge }
export type { FooterLargeProps }
